import { Box } from "@mui/joy";
import { ComponentType, useEffect, useMemo, useRef, useState } from "react";
import { FixedSizeGrid, FixedSizeGridProps } from "react-window";
import { matrixify } from "../../../lib/utils";
import { ILotto } from "../../../models";
import { LottoGridCell } from "./LottoGridCell";

const Grid = FixedSizeGrid as unknown as ComponentType<FixedSizeGridProps>;

const calcColumnsFromWidth = (width: number): number => {
  if (width > 1280) return 5;
  if (width > 700) return 3;
  return 1;
};

interface ISelectLottoGridProps {
  lotti: ILotto[];
  showMaterialName?: boolean;
  onView: (id: number) => void;
}

export default function SelectMaterialeGrid({
  lotti,
  showMaterialName,
  onView,
}: ISelectLottoGridProps) {
  const parent = useRef<Element>();
  const [gridHeight, setGridHeight] = useState<number>(0);
  const [gridWidth, setGridWidth] = useState<number>(0);
  const [columns, setColumns] = useState<number>(1);

  const matrix = useMemo(() => matrixify(lotti, columns), [lotti, columns]);

  const setGridDimensions = () => {
    const element = parent.current;
    if (!!element) {
      const { height, width } = element.getBoundingClientRect();
      setGridHeight(height);
      setGridWidth(width);
      setColumns(calcColumnsFromWidth(width));
    }
  };
  const columnWidth = useMemo(
    () => (gridWidth - 20) / columns,
    [gridWidth, columns]
  );
  useEffect(() => {
    if (parent.current) {
      const observer = new ResizeObserver((_entries) => setGridDimensions());
      observer.observe(parent.current);
      setGridDimensions();
      // Cleanup function
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return (
    <Box
      sx={{
        position: "absolute",
        height: "100%",
        width: "100%",
        "& .Grid": {
          boxSizing: "border-box",
          overflowX: "hidden",
          pb: 10,
        },
      }}
      ref={parent}
    >
      <Grid
        className="Grid"
        itemData={matrix}
        columnCount={columns}
        columnWidth={columnWidth}
        height={gridHeight}
        width={gridWidth}
        rowCount={matrix.length}
        rowHeight={columnWidth}
      >
        {(props) =>
          LottoGridCell({ ...props, columns, showMaterialName, onView })
        }
      </Grid>
    </Box>
  );
}
